// "New onboarding link" (hub, 2026-10-09) -- at the top of Expirations (the onboarding area) and beside "+ New
// candidate". The form and the link it mints; every rule lives in lib/onboardingLink.js (tested against a fake client).
// The link is shown once, with Copy and "Email it" -- the Onboarding tab's own send path (a Gmail draft built by
// buildOnboardingEmail, reviewed and sent from Gmail).
import { useState } from "react";
import { EMPTY_ONBOARDING_LINK, validateOnboardingLink, createOnboardingLink } from "./lib/onboardingLink";
import { NEW_CANDIDATE_DISCIPLINES } from "./lib/newCandidate";
import { onboardLink, buildOnboardingEmail, firstNameOf, RESTRICTED } from "./lib/onboarding";
import SendLink from "./SendLink";

const label = { fontSize: 11, color: "#64748b", fontWeight: 600 };
const errStyle = { fontSize: 11, color: "#b91c1c", marginTop: 3 };
const btn = (bg, fg) => ({ background: bg, color: fg, borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600 });

export function NewOnboardingLinkButton({ onOpen }) {
  return (
    <button className="nb" data-testid="new-onboarding-link-open" onClick={onOpen}
      style={{ background: "#0f766e", color: "white", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 600 }}>
      ＋ New onboarding link
    </button>
  );
}

/**
 * client   the supabase client;  start(candidateId) -> { token, expires_at } (onboardingApi.start)
 * config   onboardingConfig();   createDraft(to, subject, body) (gmailService), gmailSignedIn
 * onCreated(candidate)          the new row, for the Candidates list
 * onOpenExisting(candidate)     the duplicate guard: open that candidate's Onboarding tab
 */
export function NewOnboardingLinkModal({ client, start, config, createDraft, gmailSignedIn = false, onCreated = () => {},
  onOpenExisting, onClose, toast = () => {}, now = () => new Date(), sendApi = null }) {
  const [form, setForm] = useState(EMPTY_ONBOARDING_LINK);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [made, setMade] = useState(null);      // { candidate, link | startError }
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const errors = validateOnboardingLink(form);
  const show = (k) => (touched && errors[k] ? <div style={errStyle} data-testid={`onb-link-error-${k}`}>{errors[k]}</div> : null);

  const save = async () => {
    setTouched(true);
    setFailure(null);
    if (Object.keys(errors).length) return;
    setBusy(true);
    try {
      const out = await createOnboardingLink({ client, start, form, now: now() });
      if (out.existing) { onOpenExisting(out.existing); return; }
      onCreated(out.candidate);
      setMade(out);
    } catch (e) {
      setFailure(e.message || "Could not create the candidate");
    } finally {
      setBusy(false);
    }
  };

  const link = made && made.link ? onboardLink(made.link.token, config.baseUrl) : null;
  const emailBlocked = !gmailSignedIn ? "Connect Gmail to create the draft."
    : (config.missing && config.missing.length) ? `Email needs ${config.missing.join(", ")} in the build environment.` : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast("Link copied"); } catch { toast("Copy failed — select the link and copy it"); }
  };
  const emailIt = async () => {
    setBusy(true);
    try {
      const c = made.candidate;
      const { subject, body } = buildOnboardingEmail({ firstName: firstNameOf(c.name), link, expiresAt: made.link.expires_at, config });
      await createDraft(c.email, subject, body);
      toast("Draft created in Gmail — review it there before sending");
    } catch (e) {
      toast(e.message || "Could not create the draft");
    } finally {
      setBusy(false);
    }
  };

  const input = (k, l, props = {}) => (
    <div>
      <label style={label} htmlFor={`ol-${k}`}>{l}</label>
      <input id={`ol-${k}`} data-testid={`onb-link-${k}`} className="input" value={form[k]} disabled={busy || !!made}
        onChange={(e) => set(k, e.target.value)} style={{ marginTop: 4 }} {...props} />
      {show(k)}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0008", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1001 }} onClick={onClose}>
      <div className="card" data-testid="new-onboarding-link-modal" style={{ width: "min(520px, calc(100vw - 24px))", maxHeight: "85vh", overflow: "auto", padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>New onboarding link</h3>
        <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
          Creates the candidate and their onboarding link in one step. No lead, no texts: the agents never contact them.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          {input("firstName", "First name")}
          {input("lastName", "Last name")}
          {input("email", "Email", { type: "email" })}
          <div>
            <label style={label} htmlFor="ol-discipline">Discipline (optional)</label>
            <select id="ol-discipline" data-testid="onb-link-discipline" value={form.discipline} disabled={busy || !!made}
              onChange={(e) => set("discipline", e.target.value)} style={{ marginTop: 4, width: "100%" }}>
              <option value="">—</option>
              {NEW_CANDIDATE_DISCIPLINES.map((d) => <option key={d}>{d}</option>)}
            </select>
            {show("discipline")}
          </div>
          {input("phone", "Mobile (optional)", { type: "tel", placeholder: "+1 555 010 0123" })}
        </div>
        {failure && <div data-testid="onb-link-failure" style={{ ...errStyle, fontSize: 12, marginTop: 12 }}>{failure}</div>}

        {/* EXAMS-SEND-2026-10-16: one Send dialog -- copy, text, email (the company address, not a Gmail draft) */}
        {made && link && sendApi && (
          <div data-testid="onb-link-made">
            <p style={{ fontSize: 12, color: "#15803d", margin: "12px 0 0" }}>Candidate created.</p>
            <SendLink kind="onboarding" link={link} linkRef={made.link.session_id} candidate={made.candidate} api={sendApi} toast={toast} />
          </div>
        )}
        {made && link && !sendApi && (
          <div data-testid="onb-link-made" style={{ marginTop: 16, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: 12 }}>
            <p style={{ fontSize: 12, color: "#1d4ed8", margin: "0 0 6px" }}>Candidate created. Shown once: copy it now or email it.</p>
            <code data-testid="onb-link-url" style={{ wordBreak: "break-all", fontSize: 13 }}>{link}</code>
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <button className="nb" data-testid="onb-link-copy" onClick={copy} style={btn("#f1f5f9", "#334155")}>Copy</button>
              <button className="nb" data-testid="onb-link-email-it" disabled={!!emailBlocked || busy} onClick={emailIt}
                style={{ ...btn("#1d4ed8", "white"), opacity: emailBlocked ? 0.45 : 1 }}>Email it</button>
            </div>
            {emailBlocked && <p data-testid="onb-link-email-blocked" style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>{emailBlocked}</p>}
          </div>
        )}
        {made && !link && (
          <div data-testid="onb-link-start-failed" style={{ ...errStyle, fontSize: 12, marginTop: 12 }}>
            {`Candidate created, but the link was not started: ${made.startError === "denied" ? RESTRICTED : made.startError}`}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="nb" data-testid="onb-link-close" onClick={onClose} disabled={busy} style={btn("#f1f5f9", "#475569")}>{made ? "Done" : "Cancel"}</button>
          {!made && (
            <button className="nb" data-testid="onb-link-save" onClick={save} disabled={busy} style={btn("#0f766e", "white")}>
              {busy ? "Creating…" : "Create and start onboarding"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
